import { useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import {
  MapPin,
  Tag,
  Paintbrush,
  Maximize2,
  Image,
} from "lucide-react";

interface PillTabBarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const tabs = [
  { id: "map", label: "Map", icon: MapPin },
  { id: "labels", label: "Labels", icon: Tag },
  { id: "palette", label: "Palette", icon: Paintbrush },
  { id: "size", label: "Size", icon: Maximize2 },
  { id: "frames", label: "Frames", icon: Image },
];

const PillTabBar = ({ activeTab, onTabChange }: PillTabBarProps) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      let nextIndex: number | null = null;

      if (e.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (e.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (e.key === "Home") {
        nextIndex = 0;
      } else if (e.key === "End") {
        nextIndex = tabs.length - 1;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        tabRefs.current[nextIndex]?.focus();
        onTabChange(tabs[nextIndex].id);
      }
    },
    [onTabChange]
  );

  return (
    <div
      className="flex gap-2 overflow-x-auto"
      role="tablist"
      aria-label="Customizer sections"
    >
      {tabs.map((tab, index) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            ref={(el) => { tabRefs.current[index] = el; }}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              "flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sage-400)]",
              isActive
                ? "text-white"
                : "text-[var(--brand-muted)] hover:bg-[var(--sage-50)]"
            )}
            style={{
              fontFamily: "var(--font-body)",
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
              ...(isActive
                ? {
                    backgroundColor: "var(--sage-400)",
                    boxShadow: "0 2px 12px rgba(123, 158, 135, 0.3)",
                  }
                : {}),
            }}
          >
            <Icon className="w-4 h-4" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default PillTabBar;
